import { useState, useEffect, useRef, useCallback } from 'react';
import Editable from '../Editable';
import { useSection } from '../useSection';
import { authFetch } from '../../auth';

const API = '/api/cms/offices';

/** 오시는 길 — 오피스(본사/지사)를 DB에서 불러와 편집·저장. 헤더 문구는 contact/ui 섹션. */
export default function Contact({ onRegisterSave, onRegisterDirty, refreshKey = 0 }) {
  const [items, setItems] = useState(null);
  const ref = useRef([]);
  const dirtyRef = useRef(false);
  const { data: ui, setField, saveSection, isDirty: uiIsDirty } = useSection('contact', 'ui', 1);

  useEffect(() => {
    let alive = true;
    authFetch(`${API}?lang=ko`).then((r) => r.json())
      .then((d) => { if (alive) { ref.current = d; setItems(d); } })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [refreshKey]);

  const update = (i, f, v) => { dirtyRef.current = true; ref.current = ref.current.map((x, idx) => (idx === i ? { ...x, [f]: v } : x)); setItems(ref.current); };

  const save = useCallback(async () => {
    for (const o of ref.current) {
      const res = await authFetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intNum: o.intNum, name: o.name, addr: o.addr, email: o.email, tel: o.tel, fax: o.fax, mapQuery: o.mapQuery, sort: o.sort }),
      });
      if (!res.ok) throw new Error(`오피스 저장 실패 (${res.status})`);
    }
    await saveSection();
    dirtyRef.current = false;
  }, [saveSection]);

  useEffect(() => { if (onRegisterSave) onRegisterSave(save); return () => { if (onRegisterSave) onRegisterSave(null); }; }, [save, onRegisterSave]);

  const isDirty = useCallback(() => dirtyRef.current || uiIsDirty(), [uiIsDirty]);
  useEffect(() => { if (onRegisterDirty) onRegisterDirty(isDirty); return () => { if (onRegisterDirty) onRegisterDirty(null); }; }, [isDirty, onRegisterDirty]);

  if (items === null) {
    return <section className="section pt-page"><div className="container"><p className="heading-sub color-muted">불러오는 중…</p></div></section>;
  }

  return (
    <section className="section pt-page">
      <div className="container">
        <Editable as="h1" className="heading-page" value={ui.title || '오시는 길'} onChange={(t) => setField('title', t)} />
        <Editable as="p" className="heading-sub-sm" value={ui.intro || '제품 문의, 기술 상담, 파트너십 기회 등 무엇이든 당사 팀이 도와드립니다.'} onChange={(t) => setField('intro', t)} />
        <div className="contact-offices contact-offices--row">
          {items.map((o, i) => (
            <div className="contact-office" key={o.intNum}>
              <div className="contact-office-info">
                <Editable as="h3" value={o.name} onChange={(t) => update(i, 'name', t)} />
                <Editable as="p" value={o.addr} onChange={(t) => update(i, 'addr', t)} />
                <ul className="contact-info-list">
                  {o.email ? <li><Editable className="link-underline" value={o.email} onChange={(t) => update(i, 'email', t)} /></li> : null}
                  <li><Editable className="link-underline" value={o.tel} onChange={(t) => update(i, 'tel', t)} /></li>
                  <li><Editable value={o.fax} onChange={(t) => update(i, 'fax', t)} /></li>
                </ul>
              </div>
              {o.mapQuery && (
                <iframe
                  className="contact-map"
                  title={`${o.name} 위치`}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(o.mapQuery)}&hl=ko&z=17&output=embed`}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
